import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { getSupplierApiRoute, SupplierApiRouteId } from './supplier-api-tester.catalog';
import {
  SupplierApiBillable,
  SupplierApiBillableLine,
  SupplierApiCall,
  SupplierApiIdentity,
  SupplierApiListQuery,
  SupplierApiOrder,
  SupplierApiOrderLine,
  SupplierApiTicket,
  SupplierApiTicketLine,
} from './supplier-api-tester.models';
import { SupplierApiTesterResult, SupplierApiTesterService } from './supplier-api-tester.service';

/**
 * Façade typée de l'API fournisseur pour le parcours guidé : une méthode par route du catalogue.
 * Chaque appel passe par `SupplierApiTesterService.send()` et apparaît donc dans le journal.
 */
@Injectable({
  providedIn: 'root',
})
export class SupplierApiTesterClient {
  constructor(private readonly supplierApiTesterService: SupplierApiTesterService) {}

  me(): Observable<SupplierApiCall<SupplierApiIdentity>> {
    return this.call('me');
  }

  listTickets(query: SupplierApiListQuery): Observable<SupplierApiCall<SupplierApiTicket[]>> {
    return this.call('tickets-list', {}, { page: query.page, limit: query.limit, apiStatus: query.status });
  }

  getTicket(ticketId: string): Observable<SupplierApiCall<SupplierApiTicket>> {
    return this.call('tickets-detail', { ticketId });
  }

  getTicketLines(ticketId: string, query: SupplierApiListQuery): Observable<SupplierApiCall<SupplierApiTicketLine[]>> {
    return this.call('tickets-lines', { ticketId }, { page: query.page, limit: query.limit });
  }

  confirmTicket(ticketId: string, body: object): Observable<SupplierApiCall<SupplierApiTicket>> {
    return this.call('tickets-confirm', { ticketId }, {}, body);
  }

  confirmTicketLine(
    ticketId: string,
    lineId: string,
    body: object,
  ): Observable<SupplierApiCall<SupplierApiTicketLine>> {
    return this.call('tickets-line-confirm', { ticketId, lineId }, {}, body);
  }

  completeTicketLine(
    ticketId: string,
    lineId: string,
    body: object,
  ): Observable<SupplierApiCall<SupplierApiTicketLine>> {
    return this.call('tickets-line-complete', { ticketId, lineId }, {}, body);
  }

  listOrders(query: SupplierApiListQuery): Observable<SupplierApiCall<SupplierApiOrder[]>> {
    return this.call('orders-list', {}, { page: query.page, limit: query.limit, status: query.status });
  }

  getOrder(orderId: string): Observable<SupplierApiCall<SupplierApiOrder>> {
    return this.call('orders-detail', { orderId });
  }

  getOrderLines(orderId: string, query: SupplierApiListQuery): Observable<SupplierApiCall<SupplierApiOrderLine[]>> {
    return this.call('orders-lines', { orderId }, { page: query.page, limit: query.limit });
  }

  confirmOrder(orderId: string, body: object): Observable<SupplierApiCall<SupplierApiOrder>> {
    return this.call('orders-confirm', { orderId }, {}, body);
  }

  listBillables(orderId: string, query: SupplierApiListQuery): Observable<SupplierApiCall<SupplierApiBillable[]>> {
    return this.call('billables-list', { orderId }, { page: query.page, limit: query.limit });
  }

  getBillable(orderId: string, billableId: string): Observable<SupplierApiCall<SupplierApiBillable>> {
    return this.call('billables-detail', { orderId, billableId });
  }

  getBillableLines(
    orderId: string,
    billableId: string,
    query: SupplierApiListQuery,
  ): Observable<SupplierApiCall<SupplierApiBillableLine[]>> {
    return this.call('billables-lines', { orderId, billableId }, { page: query.page, limit: query.limit });
  }

  private call<T>(
    routeId: SupplierApiRouteId,
    pathParams: Record<string, string> = {},
    queryParams: Record<string, string | number> = {},
    body?: object,
  ): Observable<SupplierApiCall<T>> {
    return this.supplierApiTesterService
      .send({ route: getSupplierApiRoute(routeId), pathParams, queryParams, body })
      .pipe(map(result => toSupplierApiCall<T>(result)));
  }
}

/** Déballe l'enveloppe `{ success, data, pagination }` ou extrait le message d'erreur `{ error: { message } }`. */
function toSupplierApiCall<T>(result: SupplierApiTesterResult): SupplierApiCall<T> {
  const ok = result.status >= 200 && result.status < 300;
  const body = result.body as any;

  if (ok) {
    // `/me` est la seule route sans enveloppe.
    const enveloped = !!body && typeof body === 'object' && 'success' in body;
    return {
      ok,
      status: result.status,
      data: enveloped ? body.data : body,
      pagination: enveloped ? (body.pagination ?? null) : null,
      errorMessage: null,
    };
  }

  const fallback = result.status ? `Erreur HTTP ${result.status}` : "L'API ne répond pas.";
  return {
    ok,
    status: result.status,
    data: null,
    pagination: null,
    errorMessage: body?.error?.message ?? body?.message ?? fallback,
  };
}
